import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { Box, Button, Dialog, DialogContent, DialogTitle, Divider, IconButton, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNotification } from '../../store/redux/hooks'
import { getIsAdmin, getIsLeader, getIsManagement } from '../../store/redux/reducers/currentUserSlice'
import { useGroupAcknowledgeVacationsMutation } from '../../store/redux/reducers/upcomingVacationApiSlice'
import { useGetUserByIdQuery } from '../../store/redux/reducers/usersSlice'
import {
  useAcknowledgeVacationMutation,
  useCancelVacationAcknowledgementMutation,
  useGetVacationAcknowledgementQuery,
} from '../../store/redux/reducers/vacationAcknowledgementApiSlice'
import { User } from '../../store/types'
import DateFormat from '../../store/types/enums/DateFormat'
import RoleType from '../../store/types/enums/RoleType'
import { EmployeeVacation, VacationAcknowledgement } from '../../store/types/utils/VacationTypes'
import { createErrorMessage, hasRole } from '../../store/utils'
import { formatStringDate } from '../../store/utils/dateUtils'

interface ViewVacationDialogProps {
  onClose: () => void
  isOpen: boolean
  selectedEmployee: EmployeeVacation | null
  description: string | null
  setChoiceAnchor: (event: React.MouseEvent<HTMLButtonElement>) => void
  vacationId: string | null
  vacationIds?: string[]
  displayStart: string | null
  displayEnd: string | null
  currentUser: User | null
  createdAt?: string | null
}

const ViewVacationDialog = forwardRef<HTMLDivElement, ViewVacationDialogProps>(
  (
    {
      onClose,
      isOpen,
      selectedEmployee,
      description,
      setChoiceAnchor,
      vacationId,
      vacationIds,
      displayStart,
      displayEnd,
      currentUser,
      createdAt,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const dialogRef = useRef<HTMLDivElement>(null)
    const { displayNotification } = useNotification()
    const isLeader = useSelector(getIsLeader)
    const isAdmin = useSelector(getIsAdmin)
    const isManagement = useSelector(getIsManagement)

    const [acknowledgeSingle, { isLoading: isAckSingleLoading }] = useAcknowledgeVacationMutation()
    const [cancelSingle, { isLoading: isCancelSingleLoading }] = useCancelVacationAcknowledgementMutation()
    const [acknowledgeGroup, { isLoading: isAckGroupLoading }] = useGroupAcknowledgeVacationsMutation()

    const [isBatchCancelLoading, setBatchCancelLoading] = useState(false)

    const cleanIds: string[] = useMemo(() => {
      if (vacationIds?.length) return vacationIds.filter(Boolean)
      return vacationId ? [vacationId] : []
    }, [vacationIds, vacationId])

    const { data: firstAck } = useGetVacationAcknowledgementQuery(cleanIds[0] ?? '', {
      skip: !cleanIds.length,
    })

    const isAlreadyAcknowledged = useMemo(() => {
      if (!cleanIds.length || !firstAck) return false
      return firstAck.acknowledgements.some((ack: VacationAcknowledgement) => ack.acknowledgedBy === currentUser?.id)
    }, [cleanIds, firstAck, currentUser?.id])

    const { data: selectedEmployeeDetails } = useGetUserByIdQuery(selectedEmployee?.id ?? '', {
      skip: !isLeader || !selectedEmployee?.id || selectedEmployee?.id === currentUser?.id,
    })

    const isMyEmployee = useMemo(() => {
      if (!isLeader || !selectedEmployeeDetails || !currentUser?.id) return false
      if (selectedEmployeeDetails.id !== selectedEmployee?.id) return false
      return selectedEmployeeDetails.leaders?.some(leader => leader.id === currentUser.id) ?? false
    }, [isLeader, selectedEmployeeDetails, currentUser?.id, selectedEmployee?.id])

    const isOwnVacation = selectedEmployee?.id === currentUser?.id
    const canAcceptVacation = !isOwnVacation && (isMyEmployee || isAdmin || isManagement)
    const anyRequestInFlight = isAckSingleLoading || isCancelSingleLoading || isAckGroupLoading || isBatchCancelLoading

    const handleAcknowledge = useCallback(async () => {
      try {
        if (cleanIds.length > 1) await acknowledgeGroup(cleanIds).unwrap()
        else if (cleanIds.length === 1) await acknowledgeSingle(cleanIds[0]).unwrap()

        displayNotification({ message: t('page.vacationView.messages.vacationAcknowledgeSuccess'), type: 'success' })
      } catch (exception) {
        displayNotification({ message: createErrorMessage(exception), type: 'error' })
      }
    }, [acknowledgeGroup, acknowledgeSingle, cleanIds, displayNotification, t])

    const handleCancelAcknowledge = useCallback(async () => {
      try {
        if (cleanIds.length > 1) {
          setBatchCancelLoading(true)
          await Promise.all(cleanIds.map(id => cancelSingle(id).unwrap()))
        } else if (cleanIds.length === 1) {
          await cancelSingle(cleanIds[0]).unwrap()
        }

        displayNotification({
          message: t('page.vacationView.messages.vacationAcknowledgeCancelSuccess'),
          type: 'success',
        })
      } catch (exception) {
        displayNotification({ message: createErrorMessage(exception), type: 'error' })
      } finally {
        setBatchCancelLoading(false)
      }
    }, [cancelSingle, cleanIds, displayNotification, t])

    useImperativeHandle(ref, () => dialogRef.current as HTMLDivElement)

    return (
      <Dialog
        onClose={anyRequestInFlight ? undefined : onClose}
        open={isOpen}
        fullWidth
        PaperProps={{ sx: { width: { xs: '100%', md: '50%' } } }}
        ref={dialogRef}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
            {t('page.vacationView.vacationDetails')}
          </Typography>
          <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
          {currentUser?.id === selectedEmployee?.id || hasRole([RoleType.Management], currentUser?.roles) ? (
            <IconButton aria-label="more" onClick={setChoiceAnchor} sx={{ position: 'absolute', right: 40, top: 8 }}>
              <MoreHorizIcon />
            </IconButton>
          ) : null}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography component="h3" variant="subtitle1" fontWeight={500}>
                {t('page.vacationView.employee')}:
              </Typography>
              <Typography variant="body1">{selectedEmployee?.name}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography component="h3" variant="subtitle1" fontWeight={500}>
                {t('page.vacationView.date')}:
              </Typography>
              <Typography variant="body1">
                {displayStart === displayEnd
                  ? `${formatStringDate(displayStart)}`
                  : `${formatStringDate(displayStart)} - ${formatStringDate(displayEnd)}`}
              </Typography>
            </Stack>
            {createdAt && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography component="h3" variant="subtitle1" fontWeight={500}>
                  {t('page.vacationView.createdAt')}:
                </Typography>
                <Typography variant="body1">
                  {dayjs
                    .utc(createdAt)
                    .local()
                    .format(DateFormat.DayMonthYear + ' HH:mm')}
                </Typography>
              </Stack>
            )}
            {description && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography component="h3" variant="subtitle1" fontWeight={500}>
                  {t('page.vacationView.description')}:
                </Typography>
                <Typography variant="body1">{description}</Typography>
              </Stack>
            )}
            {canAcceptVacation && firstAck?.acknowledgements?.length ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography component="h3" variant="subtitle1" fontWeight={500}>
                  {t('page.vacationView.acknowledgedBy')}:
                </Typography>
                {firstAck.acknowledgements.map((ack: VacationAcknowledgement) => (
                  <Stack key={ack.acknowledgedBy || ''} direction="row" spacing={1} alignItems="center">
                    <CheckCircleOutlineIcon color="success" />
                    <Typography variant="body1" mb={1}>
                      {ack.acknowledgedByName}:{' '}
                      {dayjs
                        .utc(ack.acknowledgedDate)
                        .local()
                        .format(DateFormat.DayMonthYear + ' HH:mm')}
                    </Typography>
                  </Stack>
                ))}
              </>
            ) : null}
            {canAcceptVacation && cleanIds.length > 0 && (
              <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
                {!isAlreadyAcknowledged ? (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={handleAcknowledge}
                    disabled={anyRequestInFlight}
                  >
                    {t('page.vacationView.markAsAcknowledged')}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<CancelOutlinedIcon />}
                    onClick={handleCancelAcknowledge}
                    disabled={anyRequestInFlight}
                  >
                    {t('page.vacationView.cancelAcknowledgement')}
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    )
  }
)

export default ViewVacationDialog
